import * as React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  Pagination,
  Skeleton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useReservesSearch } from "./ReservesSearchProvider";
import { useNumberFormat } from "../../../../hooks/useNumberFormat";
import { LSReservesSortControl } from "../ls/LSSearchSortControl";
import { ReservesResultSkeleton } from "./ReservesSkeleton";
import { LSReservesResult } from "./ReservesResult";
import { ReservesSearchTokenDisplay } from "./ReservesSearchTokenDisplay";
import { useShowFilterSidebar } from "../LSSearchResults";
import { useSkipLinks } from "../../../../hooks/useSkipLinks";
import { useScrollToTop } from "@tlc/ui/ScrollToTop";

export const ReservesSearchResults: React.FC = () => {
  const {
    searchParams: { page },
    setPage,
    totalHits,
    results,
    loadingResults,
    pageSize,
    hasActiveFilters,
  } = useReservesSearch();

  const { translate } = useLanguage();
  const { format } = useNumberFormat();
  const showFilterSidebar = useShowFilterSidebar();

  const [scrollCallbackRef, scrollToTop] = useScrollToTop<HTMLHeadingElement>();

  const handleChangePage = (value: number) => {
    setPage(value);
    scrollToTop();
  };

  useSkipLinks([
    {
      label: translate("SkipToResults"),
      targetRef: scrollCallbackRef,
      priority: 10,
    },
  ]);

  return (
    <Box>
      {/* TODO make a re-usable component to scaffold the mobileview for the filters + sort control */}
      <>
        <Toolbar variant="dense">
          <Typography
            tabIndex={-1}
            ref={scrollCallbackRef}
            color="inherit"
            variant="h6"
            component="h2"
          >
            {loadingResults ? (
              <Skeleton
                variant="text"
                width={20}
                sx={{ display: "inline-flex" }}
              />
            ) : (
              totalHits && format(totalHits)
            )}{" "}
            {translate("reserves")}
          </Typography>
        </Toolbar>

        <Collapse in={!showFilterSidebar || hasActiveFilters}>
          <Toolbar
            sx={{
              pb: 1,
            }}
            variant="dense"
          >
            <ReservesSearchTokenDisplay />
          </Toolbar>
        </Collapse>

        <Toolbar
          sx={{
            justifyContent: "flex-end",
            pb: 1,
          }}
          variant="dense"
        >
          <LSReservesSortControl />
        </Toolbar>
      </>

      {loadingResults ? (
        <>
          {Array.from({ length: pageSize }).map((_, index) => (
            <div key={index}>
              <ReservesResultSkeleton />
            </div>
          ))}
        </>
      ) : (
        <>
          {totalHits && totalHits > 0 ? (
            results &&
            results.map((result, index) => (
              <div key={index}>
                <LSReservesResult result={result} />
              </div>
            ))
          ) : (
            <Alert severity="info">
              <AlertTitle>{translate("searchErrorMessage")}</AlertTitle>
            </Alert>
          )}
        </>
      )}
      {totalHits && totalHits > pageSize && (
        <Pagination
          page={page}
          count={Math.ceil(totalHits / pageSize)}
          onChange={(_: React.ChangeEvent<unknown>, value: number) =>
            handleChangePage(value)
          }
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingTop: "30px",
            paddingBottom: "50px",
          }}
        />
      )}
    </Box>
  );
};
